/**
 * LumvinWood — product page behaviour.
 *
 * Two custom elements, both progressive enhancements over server-rendered
 * markup in sections/lumvin-pdp-main.liquid:
 *
 *  <lumvin-gallery>     scroll-snap carousel: dots / thumbnails / arrows /
 *                       keyboard, video pause on slide change, a plain
 *                       <dialog> lightbox with prev/next (plus swipe and
 *                       drag-down-to-close on touch, see lightboxTouch),
 *                       and "jump to the variant's image" on Horizon's
 *                       productSelect event.
 *                       Only the active slide's frame is tabbable; arrow
 *                       keys move focus with the slide so keyboard users
 *                       never sit on an off-screen button.
 *
 *  <lumvin-sticky-bar>  fixed bottom bar. Shown when the real buy buttons
 *                       are off screen (mobile: whenever; desktop: only once
 *                       they have scrolled past), hidden while the footer is
 *                       in view. While the buy box is still below the fold
 *                       on a product with options the CTA reads "Choose
 *                       options" and scrolls to the picker — adding a
 *                       default variant of a made-to-order piece the shopper
 *                       has not configured is a return waiting to happen.
 *                       On variant change it swaps its inner markup for the
 *                       freshly rendered one Horizon fetched, so price /
 *                       variant title / availability always match what
 *                       Liquid rendered. The CTA drives Horizon's real
 *                       add-to-cart button, so the cart drawer, events and
 *                       error handling stay Horizon's.
 *
 * Note: Horizon scrolls `.page-wrapper` on desktop, not the window. Every
 * geometry check here uses IntersectionObserver / getBoundingClientRect,
 * never window.scrollY.
 */
import { StandardEvents } from '@shopify/events';

const DESKTOP = window.matchMedia('(min-width: 990px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* --------------------------------------------------------- lightbox touch
   Shared by the gallery lightbox and the review-photo lightbox (both are a
   plain <dialog> with one <img>). Adds what a native image viewer has:

   - horizontal swipe steps to the previous / next image. Distance OR a
     quick flick commits; the image follows the finger until then;
   - dragging down closes. The image tracks the finger and the dim behind
     it fades (`--lw-lb-dim` on the dialog); past the threshold the image
     slides off and the dialog closes, otherwise it snaps back. Dragging up
     only nudges with resistance — up is not a close gesture;
   - the axis locks on the first few px of movement, so a sloppy diagonal
     drag never does both;
   - two fingers = pinch. The browser keeps its page zoom (touch-action:
     pinch-zoom) and we drop the gesture; while the page is zoomed in,
     single-finger pans are left to the browser as well;
   - while open, the page behind is locked. Horizon scrolls .page-wrapper
     on desktop and iOS rubber-bands through overflow:hidden, so the html
     class is backed by a single-finger touchmove / wheel block on the
     dialog itself.

   Buttons inside the dialog are untouched; mouse drags are ignored so
   click-to-close on the backdrop keeps working on desktop. Esc / arrows /
   focus stay with the native <dialog>. */

const SWIPE_DIST = 48; // px of travel that commits a horizontal swipe
const SWIPE_VELOCITY = 0.45; // px/ms — a flick shorter than SWIPE_DIST still commits
const CLOSE_DIST = 110; // px of downward travel that closes
const AXIS_LOCK = 8; // px moved before the gesture commits to an axis
const LOCK_CLASS = 'lw-lightbox-open';

const viewportScale = () => window.visualViewport?.scale || 1;

const lightboxTouch = (dialog, img, step) => {
  const pointers = new Set();
  let active = null; // pointerId of the finger we follow
  let x0 = 0;
  let y0 = 0;
  let t0 = 0;
  let dx = 0;
  let dy = 0;
  let axis = null; // 'x' | 'y' | null until AXIS_LOCK
  let dragged = false; // suppresses the click that follows a drag
  let raf = 0;
  let settleTimer = 0;

  const paint = () => {
    raf = 0;
    if (axis === 'x') {
      img.style.transform = `translate3d(${dx}px, 0, 0)`;
    } else if (axis === 'y') {
      const y = dy > 0 ? dy : dy * 0.3;
      const p = Math.min(1, Math.max(0, dy) / (CLOSE_DIST * 2));
      img.style.transform = `translate3d(0, ${y}px, 0) scale(${(1 - p * 0.12).toFixed(3)})`;
      dialog.style.setProperty('--lw-lb-dim', (1 - p * 0.75).toFixed(3));
    }
  };

  // Animate back to rest (or, after a step, from the side the new image
  // arrives on). The class turns transitions on for just this moment so the
  // drag itself stays glued to the finger.
  const settle = () => {
    clearTimeout(settleTimer);
    dialog.classList.add('is-settling');
    img.style.transform = '';
    img.style.opacity = '';
    dialog.style.removeProperty('--lw-lb-dim');
    settleTimer = setTimeout(() => dialog.classList.remove('is-settling'), 350);
  };

  const reset = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    active = null;
    axis = null;
    dx = 0;
    dy = 0;
  };

  // Always after reset(): lostpointercapture may fire synchronously here
  // and must find nothing left to cancel.
  const release = (id) => {
    try {
      dialog.releasePointerCapture(id);
    } catch {
      /* capture already gone */
    }
  };

  let dragTimer = 0;
  const markDragged = () => {
    // The click that trails a touch drag arrives right after pointerup; a
    // keyboard "click" on the close button much later must not be eaten.
    dragged = true;
    clearTimeout(dragTimer);
    dragTimer = setTimeout(() => {
      dragged = false;
    }, 400);
  };

  const commitStep = (dir) => {
    step(dir);
    if (REDUCED.matches) {
      img.style.transform = '';
      img.style.opacity = '';
      return;
    }
    img.style.transform = `translate3d(${dir * 28}px, 0, 0)`;
    img.style.opacity = '0.4';
    void img.offsetWidth; // flush so the settle transition has a start value
    settle();
  };

  const closeDown = () => {
    if (REDUCED.matches) {
      dialog.close();
      return;
    }
    dialog.classList.add('is-closing');
    img.style.transform = `translate3d(0, ${window.innerHeight}px, 0) scale(0.9)`;
    dialog.style.setProperty('--lw-lb-dim', '0');
    setTimeout(() => dialog.close(), 220);
  };

  const onDown = (e) => {
    if (e.pointerType === 'mouse') return;
    pointers.add(e.pointerId);
    dragged = false;
    if (pointers.size > 1) {
      // Second finger: this is a pinch, not a swipe. Let go of the image.
      if (active !== null) {
        const id = active;
        reset();
        release(id);
        settle();
      }
      return;
    }
    if (viewportScale() > 1.01) return;
    if (e.target instanceof Element && e.target.closest('button, a')) return;
    clearTimeout(settleTimer);
    dialog.classList.remove('is-settling');
    active = e.pointerId;
    x0 = e.clientX;
    y0 = e.clientY;
    t0 = e.timeStamp;
    dx = 0;
    dy = 0;
    axis = null;
    try {
      dialog.setPointerCapture(e.pointerId);
    } catch {
      /* not capturable — moves still bubble to the dialog */
    }
  };

  const onMove = (e) => {
    if (e.pointerId !== active) return;
    dx = e.clientX - x0;
    dy = e.clientY - y0;
    if (!axis) {
      if (Math.hypot(dx, dy) < AXIS_LOCK) return;
      axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      markDragged();
    }
    if (!raf) raf = requestAnimationFrame(paint);
  };

  const onUp = (e) => {
    pointers.delete(e.pointerId);
    if (e.pointerId !== active) return;
    const elapsed = Math.max(1, e.timeStamp - t0);
    const lockedAxis = axis;
    const fx = dx;
    const fy = dy;
    const id = active;
    reset();
    release(id);
    if (lockedAxis === 'x') {
      const flick = Math.abs(fx) > 20 && Math.abs(fx) / elapsed > SWIPE_VELOCITY;
      if (Math.abs(fx) > SWIPE_DIST || flick) return commitStep(fx < 0 ? 1 : -1);
      return settle();
    }
    if (lockedAxis === 'y') {
      // Closing is destructive, so a flick needs more travel than a step.
      const flick = fy > 60 && fy / elapsed > SWIPE_VELOCITY;
      if (fy > CLOSE_DIST || flick) return closeDown();
      return settle();
    }
    // A tap: nothing moved, nothing to undo.
  };

  const onCancel = (e) => {
    pointers.delete(e.pointerId);
    if (e.pointerId !== active) return;
    const id = active;
    reset();
    release(id);
    settle();
  };

  dialog.addEventListener('pointerdown', onDown);
  dialog.addEventListener('pointermove', onMove);
  dialog.addEventListener('pointerup', onUp);
  dialog.addEventListener('pointercancel', onCancel);
  dialog.addEventListener('lostpointercapture', onCancel);

  // The click that lands after a drag must not read as "backdrop tapped".
  dialog.addEventListener(
    'click',
    (e) => {
      if (!dragged) return;
      dragged = false;
      e.preventDefault();
      e.stopImmediatePropagation();
    },
    true
  );

  /* ---- page lock while open */

  const block = (e) => {
    // One finger is ours (or nothing). Two is a pinch and a zoomed-in page
    // pans with one — both stay with the browser.
    if (e.touches && (e.touches.length > 1 || viewportScale() > 1.01)) return;
    e.preventDefault();
  };

  const opened = () => {
    document.documentElement.classList.add(LOCK_CLASS);
    dialog.addEventListener('wheel', block, { passive: false });
    dialog.addEventListener('touchmove', block, { passive: false });
  };

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove(LOCK_CLASS);
    dialog.removeEventListener('wheel', block);
    dialog.removeEventListener('touchmove', block);
    clearTimeout(settleTimer);
    clearTimeout(dragTimer);
    dragged = false;
    dialog.classList.remove('is-settling', 'is-closing');
    dialog.style.removeProperty('--lw-lb-dim');
    img.style.transform = '';
    img.style.opacity = '';
    pointers.clear();
    reset();
  });

  return { opened };
};

/* ------------------------------------------------------------------ gallery */

class LumvinGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lw-track]');
    this.slides = Array.from(this.querySelectorAll('[data-lw-slide]'));
    this.frames = this.slides.map((s) => s.querySelector('[data-lw-zoom-frame]'));
    this.dots = Array.from(this.querySelectorAll('[data-lw-dots] [data-lw-goto]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-lw-thumbs] [data-lw-goto]'));
    this.counter = this.querySelector('[data-lw-counter] b');
    this.lightbox = this.parentElement?.querySelector('[data-lw-lightbox]') || null;
    this.index = 0;
    this.lbIndex = 0;

    if (!this.track || this.slides.length === 0) return;

    this.addEventListener('click', this.#onClick);
    this.track.addEventListener('keydown', this.#onKey);

    let raf = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.#syncFromScroll());
      },
      { passive: true }
    );

    // Variant with its own image: jump to it.
    const section = this.closest('.shopify-section') || this.closest('.lwp');
    section?.addEventListener(StandardEvents.productSelect, (event) => {
      if (event.target instanceof Element && event.target.closest('product-card')) return;
      event.promise
        ?.then(({ detail }) => {
          const id = detail?.resource?.featured_media?.id;
          if (!id) return;
          const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
          if (i >= 0) this.goTo(i);
        })
        .catch(() => {});
    });

    if (this.lightbox) {
      this.lightbox.addEventListener('click', this.#onLightboxClick);
      this.lightbox.addEventListener('keydown', this.#onLightboxKey);
      const img = this.lightbox.querySelector('[data-lw-lightbox-img]');
      if (img) this.touch = lightboxTouch(this.lightbox, img, (dir) => this.#lightboxStep(dir));
      // Closing lands the gallery on whatever was last viewed full screen.
      this.lightbox.addEventListener('close', () => {
        if (this.lbIndex !== this.index) this.goTo(this.lbIndex);
        this.frames[this.index]?.focus({ preventScroll: true });
      });
    }

    this.#setActive(0);
  }

  #onClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const go = t.closest('[data-lw-goto]');
    if (go) return this.goTo(Number(go.dataset.lwGoto));
    if (t.closest('[data-lw-prev]')) return this.goTo(this.index - 1);
    if (t.closest('[data-lw-next]')) return this.goTo(this.index + 1);
    const frame = t.closest('[data-lw-zoom-frame]');
    if (frame) this.#openLightbox(this.frames.indexOf(frame));
  };

  #onKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    this.goTo(e.key === 'ArrowRight' ? this.index + 1 : this.index - 1);
    // Keep focus with the slide; the old frame is now tabindex="-1" and off screen.
    const frame = this.frames[this.index] || this.slides[this.index].querySelector('button, video, iframe');
    frame?.focus({ preventScroll: true });
  };

  goTo(i) {
    const n = this.slides.length;
    if (!n) return;
    const next = ((i % n) + n) % n;
    const slide = this.slides[next];
    this.track.scrollTo({ left: slide.offsetLeft, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    this.#setActive(next);
  }

  #syncFromScroll() {
    const w = this.track.clientWidth;
    if (!w) return;
    const i = Math.round(this.track.scrollLeft / w);
    if (i !== this.index && i >= 0 && i < this.slides.length) this.#setActive(i);
  }

  #setActive(i) {
    this.index = i;
    const mark = (el, on) => {
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-selected', on ? 'true' : 'false');
    };
    this.dots.forEach((d, k) => mark(d, k === i));
    this.thumbs.forEach((t, k) => {
      mark(t, k === i);
      if (k === i) t.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
    this.frames.forEach((f, k) => {
      if (f) f.tabIndex = k === i ? 0 : -1;
    });
    if (this.counter) this.counter.textContent = String(i + 1);
    // Only one thing plays at a time.
    this.slides.forEach((s, k) => {
      if (k === i) return;
      s.querySelectorAll('video').forEach((v) => {
        if (!v.paused) v.pause();
      });
    });
  }

  /* ---- lightbox --------------------------------------------------------- */

  #openLightbox(i) {
    if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
    if (i < 0 || !this.frames[i]) return;
    this.#showLightboxImage(i);
    this.lightbox.showModal();
    this.touch?.opened();
  }

  #showLightboxImage(i) {
    const frame = this.frames[i];
    const img = this.lightbox.querySelector('[data-lw-lightbox-img]');
    if (!frame || !img) return;
    this.lbIndex = i;
    img.src = frame.dataset.lwFull || frame.querySelector('img')?.currentSrc || '';
    img.alt = frame.querySelector('img')?.alt || '';
    const counter = this.lightbox.querySelector('[data-lw-lightbox-counter]');
    if (counter) counter.textContent = `${i + 1} / ${this.slides.length}`;
  }

  // Step to the next slide that has an image frame (videos are skipped).
  #lightboxStep(dir) {
    const n = this.slides.length;
    let i = this.lbIndex;
    for (let k = 0; k < n; k += 1) {
      i = ((i + dir) % n + n) % n;
      if (this.frames[i]) {
        this.#showLightboxImage(i);
        return;
      }
    }
  }

  #onLightboxClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('[data-lw-lightbox-prev]')) return this.#lightboxStep(-1);
    if (t.closest('[data-lw-lightbox-next]')) return this.#lightboxStep(1);
    if (t === this.lightbox || t.closest('[data-lw-lightbox-close]')) this.lightbox.close();
  };

  #onLightboxKey = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.#lightboxStep(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.#lightboxStep(-1);
    }
  };
}

if (!customElements.get('lumvin-gallery')) customElements.define('lumvin-gallery', LumvinGallery);

/* --------------------------------------------------------------- sticky bar */

class LumvinStickyBar extends HTMLElement {
  #buyVisible = true;
  #buyAbove = false;
  #footerVisible = false;
  #observers = [];

  connectedCallback() {
    this.section = this.closest('.shopify-section') || this.closest('.lwp');
    this.inner = this.querySelector('[data-lw-sticky-inner]');
    if (!this.section || !this.inner) return;

    // The wrapper, not Horizon's block: on sold-out products the block is
    // display:none and an unobservable target would pin the bar open.
    const buyBlock = this.section.querySelector('.lwp-buttons') || this.section.querySelector('.buy-buttons-block');
    const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');

    if (buyBlock) {
      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.#buyVisible = entry.isIntersecting;
          this.#buyAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.#update();
        },
        { threshold: 0 }
      );
      io.observe(buyBlock);
      this.#observers.push(io);
    }

    if (footer) {
      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.#footerVisible = entry.isIntersecting;
          this.#update();
        },
        { threshold: 0 }
      );
      io.observe(footer);
      this.#observers.push(io);
    }

    DESKTOP.addEventListener('change', this.#update);
    this.addEventListener('click', this.#onClick);

    this.section.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(StandardEvents.cartError, this.#onCartError);

    this.#update();
  }

  disconnectedCallback() {
    this.#observers.forEach((o) => o.disconnect());
    this.#observers = [];
    DESKTOP.removeEventListener('change', this.#update);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartError, this.#onCartError);
    document.body.classList.remove('lw-sticky-open');
  }

  #update = () => {
    const show = !this.#buyVisible && !this.#footerVisible && (DESKTOP.matches ? this.#buyAbove : true);
    this.dataset.state = show ? 'visible' : 'hidden';
    this.dataset.position = this.#buyAbove ? 'above' : 'below';
    document.body.classList.toggle('lw-sticky-open', show);
  };

  // "Choose options" mode: options exist and the buy box is still below.
  get #chooseMode() {
    return this.dataset.options === 'true' && this.dataset.position === 'below';
  }

  #onClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element) || !t.closest('[data-lw-sticky-cta]')) return;
    if (this.#chooseMode) return this.#scrollToPicker();
    this.#add();
  };

  #scrollToPicker() {
    const picker = document.getElementById(this.dataset.pickerId || '') || this.section.querySelector('.lwp-picker');
    if (!picker) return;
    picker.scrollIntoView({ block: 'center', behavior: REDUCED.matches ? 'auto' : 'smooth' });
    // Focus the current choice so keyboard users land in the fieldset, not on nothing.
    const checked = picker.querySelector('input:checked, select');
    checked?.focus({ preventScroll: true });
  }

  #realButton() {
    return this.section.querySelector('product-form-component [ref="addToCartButton"]');
  }

  #add() {
    const btn = this.#realButton();
    const cta = this.querySelector('[data-lw-sticky-cta]');
    if (!btn || btn.disabled) return;
    // Personalization / engraving fields live outside the form but belong to
    // it (form= attribute). A required engraving text left empty must stop
    // here with the browser's own message, not leave the bar stuck "busy".
    const form = btn.form || this.section.querySelector('product-form-component form');
    if (form && !form.checkValidity()) {
      form.reportValidity();
      return;
    }
    btn.dataset.puppet = 'true';
    cta?.classList.add('is-busy');
    btn.click();
  }

  #settle = (added) => {
    const btn = this.#realButton();
    if (btn) btn.dataset.puppet = 'false';
    const cta = this.querySelector('[data-lw-sticky-cta]');
    if (!cta) return;
    cta.classList.remove('is-busy');
    if (added) {
      cta.classList.add('is-added');
      setTimeout(() => cta.classList.remove('is-added'), 1600);
    }
  };

  #onCartUpdate = (event) => {
    if (event.promise instanceof Promise) {
      event.promise.then(() => this.#settle(true)).catch(() => this.#settle(false));
    } else {
      this.#settle(true);
    }
  };

  #onCartError = () => this.#settle(false);

  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;
        const fresh = html.querySelector('[data-lw-sticky-inner]');
        if (fresh) {
          this.inner.replaceWith(fresh);
          this.inner = fresh;
        }
        const variant = detail.resource;
        this.dataset.available = variant ? String(Boolean(variant.available)) : 'false';
        if (!variant) {
          // Unavailable combination: mirror what the picker shows and say so
          // on the button instead of a greyed-out "Add to cart".
          const title = this.querySelector('.lwp-sticky__variant');
          const picked = Array.from(this.section.querySelectorAll('variant-picker input:checked'))
            .map((i) => i.value)
            .filter(Boolean)
            .join(' / ');
          if (title && picked) title.textContent = picked;
          const cta = this.querySelector('[data-lw-sticky-cta]');
          if (cta) {
            cta.setAttribute('disabled', '');
            cta.setAttribute('aria-disabled', 'true');
            const label = cta.querySelector('.lwp-sticky__cta-label');
            if (label && this.dataset.labelUnavailable) label.textContent = this.dataset.labelUnavailable;
          }
        }
      })
      .catch(() => {});
  };
}

if (!customElements.get('lumvin-sticky-bar')) customElements.define('lumvin-sticky-bar', LumvinStickyBar);

/* ------------------------------------------------------- personalization
   <lumvin-options> wraps the free personalization fields and the paid
   engraving add-on (sections/lumvin-pdp-main.liquid). Every input is a plain
   form field tied to Horizon's product form with the `form=` attribute, so
   the values ride along as line-item properties with no script at all. What
   the script adds:

   - live character counters;
   - "photo before shipping": the WhatsApp / Email choice is only enabled
     (and required) while the box is ticked, so an unticked box sends nothing;
   - engraving: the text field is enabled + required only while the add-on is
     ticked; while it is ticked the express-checkout button is hidden (it
     would buy the piece without the engraving line);
   - answers product-form's `product-form:collect-addons` event with the
     engraving line (variant id, quantity = the piece's quantity, properties
     For / Engraving), which product-form.js adds in the same /cart/add.js
     request as a nested cart line under the piece;
   - shows product-form's `product-form:addon-failed` message beside the
     engraving field (the piece was added, the add-on was refused). */

class LumvinOptions extends HTMLElement {
  #variantTitle = '';
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.formId = this.dataset.formId || '';
    this.section = this.closest('.shopify-section') || this.closest('.lwp');
    this.#variantTitle = this.dataset.variantTitle || '';

    /* counters */
    this.querySelectorAll('[data-lw-count]').forEach((field) => {
      const update = () => this.#count(field);
      field.addEventListener('input', update, { signal });
      update();
    });

    /* personalization summary state */
    const note = this.querySelector('[data-lw-pers-text]');
    const details = note?.closest('details');
    if (note && details) {
      const mark = () => details.classList.toggle('has-value', note.value.trim().length > 0);
      note.addEventListener('input', mark, { signal });
      mark();
    }

    /* photo before shipping */
    const photo = this.querySelector('[data-lw-photo]');
    if (photo) {
      const syncPhoto = () => {
        const on = photo.checked;
        this.querySelectorAll('[data-lw-photo-via] input').forEach((radio) => {
          radio.disabled = !on;
          radio.required = on;
          if (!on) radio.checked = false;
        });
      };
      photo.addEventListener('change', syncPhoto, { signal });
      syncPhoto();
    }

    /* engraving */
    this.engraveToggle = this.querySelector('[data-lw-engrave-toggle]');
    this.engraveText = this.querySelector('[data-lw-engrave-text]');
    this.engraveError = this.querySelector('[data-lw-engrave-error]');
    if (this.engraveToggle && this.engraveText) {
      this.engraveToggle.addEventListener(
        'change',
        () => {
          this.#syncEngraving();
          if (this.engraveToggle.checked) this.engraveText.focus({ preventScroll: false });
        },
        { signal }
      );
      this.engraveText.addEventListener('input', () => this.#clearEngravingError(), { signal });
      this.#syncEngraving();
    }

    document.addEventListener('product-form:collect-addons', this.#onCollect, { signal });
    document.addEventListener('product-form:addon-failed', this.#onAddonFailed, { signal });
    this.section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.section?.querySelector('.lwp-buttons')?.classList.remove('lwp-buttons--addon');
  }

  #count(field) {
    const id = field.getAttribute('id');
    const out = id ? this.querySelector(`[data-lw-count-for="${id}"] b`) : null;
    if (out) out.textContent = String(field.value.length);
  }

  #syncEngraving() {
    const on = Boolean(this.engraveToggle?.checked);
    this.engraveText.disabled = !on;
    this.engraveText.required = on;
    this.engraveToggle.setAttribute('aria-expanded', on ? 'true' : 'false');
    this.section?.querySelector('.lwp-buttons')?.classList.toggle('lwp-buttons--addon', on);
    if (!on) this.#clearEngravingError();
  }

  #clearEngravingError() {
    if (!this.engraveError) return;
    this.engraveError.hidden = true;
    this.engraveError.textContent = '';
  }

  #forLabel() {
    const title = this.dataset.productTitle || '';
    return this.#variantTitle ? `${title} — ${this.#variantTitle}` : title;
  }

  #onCollect = (event) => {
    const form = event.target;
    // getAttribute, not form.id: the product form holds an <input name="id">
    // (the variant), and named controls shadow form properties — form.id is
    // that input, never the form's id string.
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.formId) return;
    if (!this.engraveToggle?.checked || !this.engraveText) return;
    const text = this.engraveText.value.trim();
    if (!text) return;
    const variantId = this.dataset.engravingId;
    if (!variantId) return;
    event.detail.addons.push({
      variantId,
      quantity: Number(event.detail.quantity) || 1,
      properties: {
        For: this.#forLabel(),
        Engraving: text,
        _addon: 'engraving',
      },
      errorMessage: this.dataset.engravingError || 'The engraving could not be added. Your piece is in the cart; write to us and we will sort the engraving out.',
    });
  };

  #onAddonFailed = (event) => {
    if (!this.engraveError) return;
    const source = event.target;
    if (source instanceof Element && this.section && !this.section.contains(source)) return;
    this.engraveError.textContent = event.detail?.message || '';
    this.engraveError.hidden = !this.engraveError.textContent;
  };

  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.productId && detail.productId !== this.dataset.productId) return;
        const variant = detail?.resource;
        if (!variant) return;
        this.#variantTitle = this.dataset.hasOptions === 'true' ? variant.title || '' : '';
      })
      .catch(() => {});
  };
}

if (!customElements.get('lumvin-options')) customElements.define('lumvin-options', LumvinOptions);

/* ------------------------------------------------------------------ reviews
   sections/lumvin-pdp-reviews.liquid. Three small enhancements over the
   server-rendered list:
   1. collapse to the first N reviews + "Show all" toggle (without JS every
      review is simply visible);
   2. a <dialog> lightbox for customer photos — focus trapped, Esc closes,
      arrows step within that review's photos; swipe / drag-down-to-close
      and the page lock behind it come from lightboxTouch above;
   3. the price-row rating badge scrolls to the section smoothly. */

const initReviews = (root) => {
  if (!root || root.dataset.lwReviewsReady === 'true') return;
  root.dataset.lwReviewsReady = 'true';

  /* 1. collapse / expand */
  const list = root.querySelector('[data-lw-review-list]');
  const toggle = root.querySelector('[data-lw-reviews-toggle]');
  if (list && toggle && list.querySelector('[data-lw-review-extra]')) {
    list.classList.add('is-collapsed');
    const more = toggle.querySelector('[data-lw-reviews-toggle-more]');
    const less = toggle.querySelector('[data-lw-reviews-toggle-less]');
    toggle.addEventListener('click', () => {
      const open = list.classList.toggle('is-collapsed') === false;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (more) more.hidden = open;
      if (less) less.hidden = !open;
      if (open) {
        // Land keyboard users on the first newly revealed review.
        const first = list.querySelector('[data-lw-review-extra]');
        if (first) {
          first.setAttribute('tabindex', '-1');
          first.focus({ preventScroll: true });
        }
      } else {
        list.scrollIntoView({ block: 'start', behavior: REDUCED.matches ? 'auto' : 'smooth' });
      }
    });
  }

  /* 2. photo lightbox */
  const dialog = root.querySelector('[data-lw-rlightbox]');
  if (dialog && typeof dialog.showModal === 'function') {
    const img = dialog.querySelector('[data-lw-rlightbox-img]');
    const counter = dialog.querySelector('[data-lw-rlightbox-counter]');
    let set = [];
    let index = 0;
    let opener = null;

    const show = (i) => {
      if (!set.length || !img) return;
      index = ((i % set.length) + set.length) % set.length;
      const btn = set[index];
      img.src = btn.dataset.full || btn.querySelector('img')?.currentSrc || '';
      img.alt = btn.dataset.alt || '';
      if (counter) counter.textContent = `${index + 1} / ${set.length}`;
    };

    const touch = img ? lightboxTouch(dialog, img, (dir) => show(index + dir)) : null;

    const open = (btn) => {
      const review = btn.closest('[data-lw-review]');
      set = Array.from((review || root).querySelectorAll('[data-lw-photo]'));
      opener = btn;
      dialog.dataset.single = set.length < 2 ? 'true' : 'false';
      show(set.indexOf(btn));
      dialog.showModal();
      touch?.opened();
      dialog.querySelector('[data-lw-rlightbox-close]')?.focus();
    };

    dialog.addEventListener('close', () => {
      if (img) img.removeAttribute('src');
      opener?.focus({ preventScroll: true });
      opener = null;
    });

    root.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const btn = t.closest('[data-lw-photo]');
      if (btn) open(btn);
    });

    dialog.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-lw-rlightbox-prev]')) return show(index - 1);
      if (t.closest('[data-lw-rlightbox-next]')) return show(index + 1);
      if (t === dialog || t.closest('[data-lw-rlightbox-close]')) dialog.close();
    });

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        show(index + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        show(index - 1);
      } else if (e.key === 'Tab') {
        // Native modal dialogs already keep focus inside; this wraps the ends
        // so Tab never reaches the browser chrome from the last button.
        const focusables = Array.from(dialog.querySelectorAll('button:not([hidden])')).filter(
          (b) => b.offsetParent !== null
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      // Esc: native <dialog> closes itself and fires 'close'.
    });
  }
};

const initRatingLinks = (scope = document) => {
  scope.querySelectorAll('[data-lw-rating-link]').forEach((a) => {
    if (a.dataset.lwReady === 'true') return;
    a.dataset.lwReady = 'true';
    a.addEventListener('click', (e) => {
      const target = document.getElementById('lwp-reviews');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ block: 'start', behavior: REDUCED.matches ? 'auto' : 'smooth' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  });
};

const initReviewsAll = (scope = document) => {
  scope.querySelectorAll('[data-lw-reviews]').forEach(initReviews);
  initRatingLinks(scope);
};

initReviewsAll(document);
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof Element) initReviewsAll(e.target);
});
